/**
 * usePlayerPreviewDrawerData — derives everything the player preview drawer
 * needs from board state (SE-5494).
 *
 * Extracted from `MainContainer` rather than inlined there: the screen is
 * already at its `max-lines-per-function` ceiling, and the derivation is pure
 * board→drawer projection with no screen concerns in it.
 *
 * The card lookup runs over the UNFILTERED `columnsData` for the same reason
 * `kanbanCards` does — a board filter must not make an open drawer forget which
 * stage its player is in, or the stage selector would render with no current
 * stage and the move would have no origin.
 * @module features/player-kanban/hooks/usePlayerPreviewDrawerData
 */

import { useMemo } from "react";

import type { ColumnData, Player } from "../types/types";
import type { PlayerPreviewStageOption } from "../components/molecules/PlayerPreviewStagePicker";

/** Minimal phase shape the stage selector needs. */
export interface PreviewPhase {
  /** Phase id. */
  readonly id: string;
  /** Phase display name. */
  readonly name: string;
}

/** Inputs for {@link usePlayerPreviewDrawerData}. */
export interface PlayerPreviewDrawerDataInput {
  /** The player whose preview is open, if any. */
  readonly selectedPlayerId?: string | null;
  /** The board's UNFILTERED columns. */
  readonly columnsData?: readonly ColumnData[];
  /** The board's phases, in board order. */
  readonly phases?: readonly PreviewPhase[];
}

/** What the drawer needs from board state. */
export interface PlayerPreviewDrawerData {
  /** Stage options for the selector, in board order. */
  readonly stages: readonly PlayerPreviewStageOption[];
  /** The selected player's current stage id, when it is on the board. */
  readonly currentStageId?: string;
  /** Tag chips for the selected player's card. */
  readonly tags: readonly { id: string; name: string }[];
  /** Whole days the card has spent in its current stage (SE-5497). */
  readonly daysInStage?: number | null;
  /**
   * The selected player's KANBAN CARD id (SE-5504) — what scopes the drawer's
   * audit trail to this card via `pipelineActivityLog(kanbanCardId:)`.
   * Undefined when the player is not on the board.
   */
  readonly kanbanCardId?: string;
}

/** Stable empty result so a closed drawer never re-renders on identity churn. */
const NO_TAGS: readonly { id: string; name: string }[] = [];

/**
 * Locate a player's card and its stage across the board's columns.
 * @param columnsData - The board's unfiltered columns.
 * @param selectedPlayerId - The player to find.
 * @returns The player and its stage id, or `null` when not on the board.
 */
const findPlayerCard = (
  columnsData: readonly ColumnData[],
  selectedPlayerId: string
): { player: Player; stageId: string } | null =>
  columnsData.reduce<{ player: Player; stageId: string } | null>(
    (found, column) => {
      if (found) return found;
      const player = column.players.find(
        candidate => candidate.id === selectedPlayerId
      );
      return player ? { player, stageId: column.id } : null;
    },
    null
  );

/**
 * Derive the preview drawer's board-sourced props.
 * @param input - {@link PlayerPreviewDrawerDataInput}
 * @param input.selectedPlayerId - The player whose preview is open, if any
 * @param input.columnsData - The board's UNFILTERED columns
 * @param input.phases - The board's phases, in board order
 * @returns The stages, current stage, tags and days-in-stage the drawer renders.
 */
export const usePlayerPreviewDrawerData = ({
  selectedPlayerId,
  columnsData,
  phases,
}: PlayerPreviewDrawerDataInput): PlayerPreviewDrawerData => {
  // Nullish-guarded: both inputs are optional on the board hook's contract and
  // are absent while a board resolves, where an unguarded read would crash the
  // whole screen rather than render an empty drawer.
  const stages = useMemo(
    () => (phases ?? []).map(phase => ({ id: phase.id, name: phase.name })),
    [phases]
  );

  const card = useMemo(
    () =>
      selectedPlayerId && columnsData
        ? findPlayerCard(columnsData, selectedPlayerId)
        : null,
    [selectedPlayerId, columnsData]
  );

  const tags = useMemo(
    () =>
      card
        ? (card.player.tags ?? []).map(tag => ({ id: tag.id, name: tag.name }))
        : NO_TAGS,
    [card]
  );

  return {
    stages,
    currentStageId: card?.stageId,
    tags,
    daysInStage: card?.player.daysInStage,
    kanbanCardId: card?.player.cardId,
  };
};
